// Boot orchestrator: session probe → branch per host on auth-error or
// applicative !verified → fetch account + user list in parallel.
//
// Auth-redirect ownership:
//   - HTTP auth errors (103/239/250/501) on `account.get` / `getUsersList` /
//     `session.extend` → @platform/wiservice auto-redirects. Never seen here.
//   - HTTP auth errors on `session.isverified` → endpoints.sessionIsVerified
//     sets `onAuthInterrupt`, surfaces a WsError. We branch per host below.
//   - Status:0 + body.verified:false on `session.isverified` → applicative
//     "session technically OK but server says re-auth". Manual redirect,
//     except on raw localhost where the parent-domain cookie can't make it
//     back from the portal.

import { WsError } from '@/lib/wiservice';
import { redirectToAccount, checkIsWithinger } from '@/lib/account';
import { Constants, AUTH_ERROR_CODES } from '@/config/constants';
import * as endpoints from '../endpoints';
import type { AccountData, UserData } from '../endpoints';

export interface CurrentUserData {
  account: AccountData | null;
  currentUser: UserData | null;
  isWithinger: boolean;
}

function isLocalhostNoVhost(): boolean {
  return Constants.ENV === 'local' && window.location.hostname === 'localhost';
}

function isAuthError(err: unknown): err is WsError {
  return err instanceof WsError && (AUTH_ERROR_CODES as readonly number[]).includes(err.status);
}

// Sentinel returned after redirectToAccount() — the page is unloading,
// no consumer renders against this value. Keeps the TanStack query in a
// non-error success state during the navigation race.
const REDIRECT_SENTINEL: CurrentUserData = {
  account: null,
  currentUser: null,
  isWithinger: false,
};

export async function fetchCurrentUser(): Promise<CurrentUserData> {
  let session;
  try {
    session = await endpoints.sessionIsVerified();
  } catch (err) {
    if (isAuthError(err)) {
      if (isLocalhostNoVhost()) {
        throw err;
      }
      redirectToAccount();
      return REDIRECT_SENTINEL;
    }
    throw err;
  }

  if (session.body?.verified === false) {
    if (isLocalhostNoVhost()) {
      throw new Error(
        'Session not verified — please log in via the corp vhost or paste a session key.',
      );
    }
    redirectToAccount();
    return REDIRECT_SENTINEL;
  }

  const [accountResp, users] = await Promise.all([
    endpoints.get(),
    endpoints.getUsersList(),
  ]);
  const currentUser = users.length > 0 ? users[0] ?? null : null;
  const { account } = accountResp;

  return {
    account,
    currentUser,
    isWithinger: checkIsWithinger(account?.email),
  };
}
